/**
 * Correspondences — directApiClient (LF-3216)
 *
 * `revalidatePath` calls dropped — invalidation now happens via React Query
 * at the hook layer (see hooks.tsx + query-dependencies.ts `correspondence`).
 */

'use client';

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import {
  Correspondence,
  CorrespondenceApiResponse,
  CorrespondenceListResponse,
  CreateCorrespondenceRequest,
  BulkCreateCorrespondenceRequest,
  UpdateCorrespondenceRequest,
  IncomingEmailRequest,
  CorrespondenceActionResult,
  CreateCorrespondenceRequestSchema,
  BulkCreateCorrespondenceRequestSchema,
  UpdateCorrespondenceRequestSchema,
  IncomingEmailRequestSchema,
  mapApiResponseToCorrespondence
} from './types';

function toErrorResult<T>(error: unknown): CorrespondenceActionResult<T> {
  if (error instanceof DirectApiError) {
    return { success: false, error: error.message };
  }
  return {
    success: false,
    error: error instanceof Error ? error.message : 'Request failed'
  };
}

export async function getMatterCorrespondencesAction(
  matterId: number
): Promise<CorrespondenceActionResult<CorrespondenceListResponse>> {
  try {
    const response = await directApiClient.get<CorrespondenceApiResponse[]>(
      `/matters/${matterId}/correspondences`
    );

    const apiResponses = Array.isArray(response) ? response : [];
    const correspondences = apiResponses.map(mapApiResponseToCorrespondence);

    return {
      success: true,
      data: { correspondences, total: correspondences.length }
    };
  } catch (error) {
    return toErrorResult<CorrespondenceListResponse>(error);
  }
}

export async function getCorrespondenceByInstitution(
  matterId: number,
  institutionId: number
): Promise<Correspondence[]> {
  try {
    const response = await directApiClient.get<CorrespondenceApiResponse[]>(
      `/matters/${matterId}/correspondences?institutionId=${institutionId}`
    );
    const apiResponses = Array.isArray(response) ? response : [];
    return apiResponses.map(mapApiResponseToCorrespondence);
  } catch (error) {
    console.error('[getCorrespondenceByInstitution] Error:', error);
    return [];
  }
}

export async function getCorrespondenceAction(
  matterId: number,
  correspondenceId: number
): Promise<CorrespondenceActionResult<Correspondence>> {
  try {
    const response = await directApiClient.get<CorrespondenceApiResponse>(
      `/matters/${matterId}/correspondences/${correspondenceId}`
    );
    return { success: true, data: mapApiResponseToCorrespondence(response) };
  } catch (error) {
    return toErrorResult<Correspondence>(error);
  }
}

export async function createCorrespondenceAction(
  matterId: number,
  data: CreateCorrespondenceRequest
): Promise<CorrespondenceActionResult<Correspondence>> {
  try {
    const validationResult = CreateCorrespondenceRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const correspondence = await directApiClient.post<Correspondence>(
      `/matters/${matterId}/correspondences`,
      validationResult.data
    );
    return { success: true, data: correspondence };
  } catch (error) {
    return toErrorResult<Correspondence>(error);
  }
}

export async function bulkCreateCorrespondencesAction(
  matterId: number,
  data: BulkCreateCorrespondenceRequest
): Promise<CorrespondenceActionResult<Correspondence[]>> {
  try {
    const validationResult =
      BulkCreateCorrespondenceRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const responses = await directApiClient.post<CorrespondenceApiResponse[]>(
      `/matters/${matterId}/correspondences/bulk`,
      validationResult.data
    );

    const apiResponses = Array.isArray(responses) ? responses : [];
    return {
      success: true,
      data: apiResponses.map(mapApiResponseToCorrespondence)
    };
  } catch (error) {
    return toErrorResult<Correspondence[]>(error);
  }
}

export async function updateCorrespondenceAction(
  matterId: number,
  correspondenceId: number,
  data: UpdateCorrespondenceRequest
): Promise<CorrespondenceActionResult<Correspondence>> {
  try {
    const validationResult = UpdateCorrespondenceRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const response = await directApiClient.put<CorrespondenceApiResponse>(
      `/matters/${matterId}/correspondences/${correspondenceId}`,
      validationResult.data
    );
    return { success: true, data: mapApiResponseToCorrespondence(response) };
  } catch (error) {
    return toErrorResult<Correspondence>(error);
  }
}

export async function deleteCorrespondenceAction(
  matterId: number,
  correspondenceId: number
): Promise<CorrespondenceActionResult<void>> {
  try {
    await directApiClient.delete(
      `/matters/${matterId}/correspondences/${correspondenceId}`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}

export async function sendCorrespondenceAction(
  matterId: number,
  correspondenceId: number,
  sendSenderCopy?: boolean
): Promise<CorrespondenceActionResult<Correspondence>> {
  try {
    const response = await directApiClient.post<CorrespondenceApiResponse>(
      `/matters/${matterId}/correspondences/${correspondenceId}/send`,
      sendSenderCopy !== undefined ? { sendSenderCopy } : {}
    );
    return { success: true, data: mapApiResponseToCorrespondence(response) };
  } catch (error) {
    return toErrorResult<Correspondence>(error);
  }
}

export async function regenerateLetterAction(
  matterId: number,
  correspondenceId: number
): Promise<CorrespondenceActionResult<Correspondence>> {
  try {
    const response = await directApiClient.post<CorrespondenceApiResponse>(
      `/matters/${matterId}/correspondences/${correspondenceId}/regenerate-letter`,
      {}
    );
    return { success: true, data: mapApiResponseToCorrespondence(response) };
  } catch (error) {
    return toErrorResult<Correspondence>(error);
  }
}

export async function previewLetterAction(
  matterId: number,
  correspondenceId: number
): Promise<CorrespondenceActionResult<{ url: string }>> {
  try {
    const response = await directApiClient.get<{ url: string }>(
      `/matters/${matterId}/correspondences/${correspondenceId}/preview-letter`
    );
    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<{ url: string }>(error);
  }
}

export async function processIncomingEmailAction(
  data: IncomingEmailRequest
): Promise<CorrespondenceActionResult<Correspondence>> {
  try {
    const validationResult = IncomingEmailRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const response = await directApiClient.post<CorrespondenceApiResponse>(
      '/correspondences/incoming',
      validationResult.data
    );
    return { success: true, data: mapApiResponseToCorrespondence(response) };
  } catch (error) {
    return toErrorResult<Correspondence>(error);
  }
}
